import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSection() {
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(null);
  const [provider, setProvider] = useState("stripe");

  useEffect(() => {
    axios.get(`${API}/packages`).then((r) => setPackages(r.data.packages || [])).catch(() => {});
  }, []);

  async function payStripe(pkgId) {
    setBusy(pkgId + ":stripe");
    try {
      const { data } = await axios.post(`${API}/payments/stripe/checkout`, {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start Stripe checkout. Please try again.");
      setBusy(null);
    }
  }

  async function payRazorpay(pkgId) {
    setBusy(pkgId + ":razorpay");
    try {
      const { data } = await axios.post(`${API}/payments/razorpay/order`, { package_id: pkgId });
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        const opts = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          order_id: data.order_id,
          name: "Metamorphosis Institute of Transformation",
          description: data.name,
          theme: { color: "#D4AF37" },
          handler: async (r) => {
            try {
              await axios.post(`${API}/payments/razorpay/verify`, {
                razorpay_order_id: r.razorpay_order_id,
                razorpay_payment_id: r.razorpay_payment_id,
                razorpay_signature: r.razorpay_signature,
              });
              window.location.href = `/payment/success?session_id=${r.razorpay_order_id}`;
            } catch {
              toast.error("Payment verification failed.");
            }
          },
          modal: { ondismiss: () => setBusy(null) },
        };
        // eslint-disable-next-line no-undef
        const rzp = new window.Razorpay(opts);
        rzp.open();
      };
      document.body.appendChild(script);
    } catch (e) {
      const msg = e.response?.data?.detail || "Razorpay not available yet.";
      toast.error(msg);
      setBusy(null);
    }
  }

  return (
    <section id="payment" className="relative py-32 md:py-48" data-testid="payment-section">
      <div className="container-mit">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16">
          <div className="md:col-span-8">
            <p className="text-[11px] tracking-[0.4em] text-[#A4801C] mb-4">— BOOK A PROGRAM</p>
            <h2 className="font-display text-5xl md:text-7xl font-light leading-none text-[#0A0A14]">
              Reserve your <em className="text-gold-grad not-italic">transformation.</em>
            </h2>
            <p className="text-[#0A0A14]/70 max-w-xl mt-8 text-lg leading-relaxed">
              Pay a deposit or book a paid consultation. All payments are secured — refundable up to 48 hours before the scheduled session.
            </p>
          </div>
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 justify-end" data-testid="provider-toggle">
              <button
                data-testid="provider-stripe"
                onClick={() => setProvider("stripe")}
                className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase border ${provider === "stripe" ? "bg-[#1E1B4B] text-black border-[#1E1B4B]" : "border-[#0A0A14]/18 text-[#0A0A14]/70"}`}
              >
                Stripe · USD
              </button>
              <button
                data-testid="provider-razorpay"
                onClick={() => setProvider("razorpay")}
                className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase border ${provider === "razorpay" ? "bg-[#1E1B4B] text-black border-[#1E1B4B]" : "border-[#0A0A14]/18 text-[#0A0A14]/70"}`}
              >
                Razorpay · INR
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bento-card p-8 md:p-10 flex flex-col justify-between min-h-[320px]"
              data-testid={`pkg-${p.id}`}
            >
              <div>
                <p className="text-[10px] tracking-[0.3em] text-[#A4801C]">PACKAGE {String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-display text-3xl md:text-4xl text-[#0A0A14] mt-4">{p.name}</h3>
              </div>
              <div className="mt-10">
                <div className="font-display text-4xl text-[#0A0A14] mb-6">
                  {provider === "stripe" ? `$${p.usd}` : `₹${p.inr.toLocaleString("en-IN")}`}
                  <span className="text-sm text-[#0A0A14]/55 ml-2 font-body">
                    {provider === "stripe" ? "USD" : "INR"}
                  </span>
                </div>
                <button
                  className="btn-editorial w-full justify-center"
                  disabled={busy === `${p.id}:${provider}`}
                  onClick={() => (provider === "stripe" ? payStripe(p.id) : payRazorpay(p.id))}
                  data-testid={`pay-${provider}-${p.id}`}
                >
                  {busy === `${p.id}:${provider}` ? "Redirecting…" : `Pay via ${provider === "stripe" ? "Stripe" : "Razorpay"}`}
                  <span className="arrow">→</span>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
